import { useState, useEffect, useMemo, useRef } from 'react';
import {
  collection, doc, onSnapshot, addDoc, updateDoc, deleteDoc,
  setDoc, serverTimestamp, query, orderBy, getDocs, limit, where, writeBatch,
} from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import { generateGroupItineraryPdf } from '../utils/generateGroupItineraryPdf';
import useTourTeams from '../hooks/useTourTeams';
import { ActivityMatches, FixtureFormModal, MatchLine, useFixtureContacts } from './TeamFixtures';
import { fixtureMismatch, isFixturesActivity, itineraryDayDate, sortFixtures } from '../utils/teamFixtures';
import { skeletalToItems, withoutExistingMeals } from '../utils/skeletalToFull';
import { isOutsideTour, itemsInsideTour } from '../utils/itineraryDays';
import { useT } from '../contexts/LocaleContext';

// ── Status config ────────────────────────────────────────────────────────────

const STATUS = {
  not_required: { label: 'Auto Approved',  dot: '#22C55E', bg: '#F0FDF4', border: '#86EFAC', text: '#15803D' },
  pending:      { label: 'Pending',        dot: '#9CA3AF', bg: '#F9FAFB', border: '#D1D5DB', text: '#6B7280' },
  approved:     { label: 'Approved',       dot: '#22C55E', bg: '#F0FDF4', border: '#86EFAC', text: '#15803D' },
  overridden:   { label: 'Auto Approved',  dot: '#22C55E', bg: '#F0FDF4', border: '#86EFAC', text: '#15803D' },
  declined:     { label: 'Declined',       dot: '#EF4444', bg: '#FEF2F2', border: '#FCA5A5', text: '#DC2626' },
  question:     { label: 'Has a Question', dot: '#F59E0B', bg: '#FFFBEB', border: '#FCD34D', text: '#D97706' },
};

const NEEDS_DETAILS_DOT = '#F59E0B';

function isGreen(s) {
  return ['not_required', 'approved', 'overridden'].includes(s || 'pending');
}

// ── Helpers ──────────────────────────────────────────────────────────────────

// Covers 'Transfer', 'Transfers', 'Travel' — all mean the same thing
function isTransferLike(type) {
  return ['transfer', 'transfers', 'travel'].includes((type || '').toLowerCase());
}

// Display "Travel" wherever the old "Transfer"/"Transfers" category name appears
function displayActivityType(type) {
  if (!type) return type;
  return isTransferLike(type) ? 'Travel' : type;
}

// Items are grouped for the supplier filter by supplier id, falling back to
// the name (hotel meals and older items can carry a name without an id).
const NO_SUPPLIER = '__none__';
function supplierKey(item) {
  if (item.supplierId) return item.supplierId;
  if (item.supplierName) return `name:${item.supplierName}`;
  return NO_SUPPLIER;
}

function numDays(dep, ret) {
  if (!dep || !ret) return 3;
  const diff = Math.round((new Date(ret) - new Date(dep)) / 86400000);
  return Math.max(1, Math.min((diff || 0) + 1, 15));
}

function dayLabel(dep, idx, locale = 'en-GB') {
  if (!dep) return `Day ${idx + 1}`;
  const d = new Date(dep);
  d.setDate(d.getDate() + idx);
  return d.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });
}

// The activity type as shown, in the viewer's language when it's a known one.
function activityLabel(type, t) {
  const shown = displayActivityType(type);
  return shown ? t.value('activity', shown) : shown;
}

// Supplier fields on a meal served at the tour's hotel.
function hotelMealFields(hotel) {
  return {
    locationName:       hotel.locationName || hotel.name,
    supplierId:         hotel.id,
    supplierName:       hotel.name,
    supplierEmail:      hotel.email      || null,
    supplierPhone:      hotel.phone      || null,
    supplierAddress:    hotel.address    || null,
    supplierGoogleLink: hotel.googleLink || null,
  };
}

const MEAL_DEFAULTS = [
  { title: 'Breakfast',    time: '08:00', activityType: 'Meals & Catering' },
  { title: 'Lunch',        time: '13:00', activityType: 'Meals & Catering' },
  { title: 'Evening Meal', time: '20:00', activityType: 'Meals & Catering' },
];

// ── Supplier search input ────────────────────────────────────────────────────

const BOOKING_STATUS_BADGE = {
  confirmed: { label: 'Availability confirmed', dot: '#22C55E', color: '#065F46', bg: '#ECFDF5' },
  declined:  { label: 'Availability declined',  dot: '#EF4444', color: '#991B1B', bg: '#FEF2F2' },
  pending:   { label: 'Awaiting confirmation',  dot: '#F59E0B', color: '#92400E', bg: '#FFFBEB' },
};

function SupplierSearchInput({ suppliers, selectedId, onSelect, filterByType = null, placeholder = 'Search suppliers…', bookingStatusMap = {} }) {
  const [query, setQuery] = useState('');
  const [open, setOpen]   = useState(false);
  const selected = suppliers.find((s) => s.id === selectedId);

  const base = filterByType
    ? suppliers.filter((s) => (s.serviceTypes || []).includes(filterByType))
    : suppliers;

  const filtered = query
    ? base.filter((s) => s.name.toLowerCase().includes(query.toLowerCase()))
    : base;

  const grouped = {};
  filtered.forEach((s) => {
    const cat = (s.serviceTypes || [])[0] || 'Other';
    (grouped[cat] = grouped[cat] || []).push(s);
  });

  return (
    <div className="relative">
      <input
        type="text"
        value={open ? query : (selected?.name || '')}
        onFocus={() => { setOpen(true); setQuery(''); }}
        onBlur={() => setTimeout(() => setOpen(false), 160)}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={placeholder}
        className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
      />
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-[#E5E7EB] rounded-[10px] shadow-lg max-h-56 overflow-y-auto">
          {Object.keys(grouped).sort().map((cat) => (
            <div key={cat}>
              <div className="px-3 py-1.5 text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider bg-[#F9FAFB] sticky top-0">
                {cat}
              </div>
              {grouped[cat].map((s) => {
                const bookingStatus = bookingStatusMap[s.id];
                const badge = bookingStatus ? BOOKING_STATUS_BADGE[bookingStatus] : null;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onMouseDown={() => { onSelect(s); setOpen(false); setQuery(''); }}
                    className="w-full text-left px-3 py-2 text-[13px] hover:bg-blue-light/40 transition flex items-center justify-between gap-2"
                  >
                    <div className="min-w-0">
                      <span className="font-medium text-[#111827]">{s.name}</span>
                      {s.locationName && <span className="block text-[11px] text-[#9CA3AF]">{s.locationName}</span>}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {badge && (
                        <span style={{ background: badge.bg, color: badge.color }} className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">
                          <span style={{ background: badge.dot }} className="w-1.5 h-1.5 rounded-full inline-block" />
                          {badge.label}
                        </span>
                      )}
                      {s.email && <span className="text-[11px] text-[#9CA3AF] truncate max-w-[140px]">{s.email}</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
          {filtered.length === 0 && (
            <p className="px-3 py-3 text-[12px] text-[#9CA3AF] text-center">
              No suppliers found{filterByType ? ` for "${filterByType}"` : ''}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Saved-location picker ─────────────────────────────────────────────────────

function LocationSearchInput({ locations, onSelect, placeholder = 'Select saved location…' }) {
  const [query, setQuery] = useState('');
  const [open, setOpen]   = useState(false);

  const filtered = query
    ? locations.filter((l) =>
        [l.name, l.address, l.category].filter(Boolean).some((v) =>
          v.toLowerCase().includes(query.toLowerCase())))
    : locations;

  const grouped = {};
  filtered.forEach((l) => {
    const cat = l.category || 'Other';
    (grouped[cat] = grouped[cat] || []).push(l);
  });

  return (
    <div className="relative">
      <input
        type="text"
        value={open ? query : ''}
        onFocus={() => { setOpen(true); setQuery(''); }}
        onBlur={() => setTimeout(() => setOpen(false), 160)}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={placeholder}
        className="w-full border border-dashed border-[#C7D2E4] bg-[#F8FAFD] rounded-[8px] px-3 py-2 text-[12.5px] text-pb placeholder:text-pb/70 focus:outline-none focus:ring-2 focus:ring-pb"
      />
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-[#E5E7EB] rounded-[10px] shadow-lg max-h-56 overflow-y-auto">
          {Object.keys(grouped).sort().map((cat) => (
            <div key={cat}>
              <div className="px-3 py-1.5 text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider bg-[#F9FAFB] sticky top-0">
                {cat}
              </div>
              {grouped[cat].map((l) => (
                <button
                  key={l.id}
                  type="button"
                  onMouseDown={() => { onSelect(l); setOpen(false); setQuery(''); }}
                  className="w-full text-left px-3 py-2 text-[13px] hover:bg-blue-light/40 transition"
                >
                  <span className="font-medium text-[#111827]">{l.name}</span>
                  {l.address && <span className="block text-[11px] text-[#9CA3AF] truncate">{l.address}</span>}
                </button>
              ))}
            </div>
          ))}
          {filtered.length === 0 && (
            <p className="px-3 py-3 text-[12px] text-[#9CA3AF] text-center">
              {locations.length === 0
                ? 'No saved locations yet — add them on the Locations page.'
                : 'No matches.'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Toggle switch ────────────────────────────────────────────────────────────

function Toggle({ on, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      style={{ width: 40, height: 22, minWidth: 40, position: 'relative', borderRadius: 11, background: on ? '#1A4A8A' : '#D1D5DB', border: 'none', cursor: 'pointer', transition: 'background 0.2s' }}
    >
      <span style={{ position: 'absolute', top: 2, left: on ? 20 : 2, width: 18, height: 18, background: 'white', borderRadius: '50%', boxShadow: '0 1px 3px rgba(0,0,0,0.15)', transition: 'left 0.2s' }} />
    </button>
  );
}

// ── Add / Edit activity panel ─────────────────────────────────────────────────

function ItemPanel({ dayIdx, item, request, allSuppliers, allLocations = [], categories, bookingStatusMap, onSave, onClose, tour = null, fixtureContacts = {} }) {
  const isEdit = !!item;

  const [form, setForm] = useState(() => {
    if (item) {
      return {
        time: item.time || '',
        endTime: item.endTime || '',
        activityType: item.activityType || '',
        // A copied skeletal activity keeps its wording here until staff change it.
        locationName: item.locationName || (item.needsDetails ? item.title : '') || '',
        locationAddress: item.locationAddress || '',
        locationGoogleLink: item.locationGoogleLink || '',
        supplierId: item.supplierId || '',
        supplierData: item.supplierId ? {
          id: item.supplierId,
          name: item.supplierName,
          email: item.supplierEmail,
          phone: item.supplierPhone,
          address: item.supplierAddress,
          googleLink: item.supplierGoogleLink,
          locationName: item.locationName || '',
        } : null,
        sendEmail: item.sendEmail ?? true,
        notes: item.notes || '',
        overrideApproved: ['overridden', 'not_required'].includes(item.approvalStatus),
        transferMode: item.transferMode || 'bus',
        pickupLocation: item.pickupLocation || '',
        pickupAddress: item.pickupAddress || '',
        pickupGoogleLink: item.pickupGoogleLink || '',
        dropoffLocation: item.dropoffLocation || '',
        dropoffAddress: item.dropoffAddress || '',
        dropoffGoogleLink: item.dropoffGoogleLink || '',
        fixtureIds: item.fixtureIds || [],
      };
    }
    return {
      time: '', endTime: '', activityType: '', locationName: '',
      locationAddress: '', locationGoogleLink: '',
      supplierId: '', supplierData: null,
      sendEmail: true, notes: '', overrideApproved: false,
      transferMode: 'bus',
      pickupLocation: '', pickupAddress: '', pickupGoogleLink: '',
      dropoffLocation: '', dropoffAddress: '', dropoffGoogleLink: '',
      fixtureIds: [],
    };
  });
  const [fixtureModal, setFixtureModal] = useState(null); // { fixture } | { defaults }

  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const [locationEdited, setLocationEdited] = useState(false);
  const [libMsg, setLibMsg] = useState('');

  function set(key, val) { setForm((p) => ({ ...p, [key]: val })); }

  // Fill a set of fields from a chosen saved location.
  function applySavedLocation(loc, keys) {
    setForm((p) => ({
      ...p,
      [keys.name]: loc.name || '',
      [keys.address]: loc.address || '',
      [keys.google]: loc.googleLink || '',
    }));
    if (keys.name === 'locationName') setLocationEdited(true);
  }

  // Save a typed-in place to the reusable Locations library.
  async function saveToLibrary(name, address, googleLink) {
    const n = (name || '').trim();
    if (!n) return;
    setLibMsg('saving');
    try {
      await addDoc(collection(db, 'locations'), {
        name: n,
        category: '',
        address: (address || '').trim(),
        googleLink: (googleLink || '').trim(),
        notes: '',
        createdAt: serverTimestamp(),
      });
      setLibMsg('saved');
      setTimeout(() => setLibMsg(''), 2500);
    } catch (e) {
      console.error(e);
      setLibMsg('error');
      setTimeout(() => setLibMsg(''), 2500);
    }
  }

  function handleActivityTypeChange(type) {
    setForm((p) => {
      const supplierMatchesType = p.supplierId
        ? (allSuppliers.find((s) => s.id === p.supplierId)?.serviceTypes || []).includes(type)
        : true;
      return {
        ...p,
        activityType: type,
        ...(p.supplierId && !supplierMatchesType ? { supplierId: '', supplierData: null } : {}),
      };
    });
  }

  function handleSupplierSelect(s) {
    setForm((p) => ({
      ...p,
      supplierId: s.id,
      supplierData: s,
      locationName: locationEdited ? p.locationName : (s.locationName || ''),
    }));
  }

  function handleClearSupplier() {
    setForm((p) => ({ ...p, supplierId: '', supplierData: null }));
  }

  function resetLocationToSupplier() {
    const supplierLoc = form.supplierData?.locationName || '';
    setForm((p) => ({ ...p, locationName: supplierLoc, locationAddress: '', locationGoogleLink: '' }));
    setLocationEdited(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.time)         { setError('Start time is required.'); return; }
    if (!form.activityType) { setError('Activity is required.'); return; }

    setSaving(true);
    setError('');
    try {
      const sd = form.supplierData;
      const hasSupplier = !!form.supplierId;

      let approvalStatus;
      if (!hasSupplier) {
        approvalStatus = 'not_required';
      } else if (form.overrideApproved) {
        approvalStatus = 'overridden';
      } else {
        approvalStatus = (isEdit && item.approvalStatus === 'approved') ? 'approved' : 'pending';
      }

      const isTransfer = isTransferLike(form.activityType);

      await onSave({
        day: dayIdx + 1,
        time: form.time,
        endTime: form.endTime || null,
        activityType: form.activityType,
        locationName: form.locationName.trim() || null,
        title: form.locationName.trim() || form.activityType,
        // Address/map of the place itself; takes priority over supplierAddress
        // (which is the supplier's own address, e.g. a coach company's office).
        locationAddress:    form.locationAddress.trim() || null,
        locationGoogleLink: form.locationGoogleLink.trim() || null,
        itemType: hasSupplier ? 'supplier' : 'free_text',
        supplierId:         hasSupplier ? (sd?.id          || null) : null,
        supplierName:       hasSupplier ? (sd?.name        || null) : null,
        supplierEmail:      hasSupplier ? (sd?.email       || null) : null,
        supplierPhone:      hasSupplier ? (sd?.phone       || null) : null,
        supplierAddress:    hasSupplier ? (sd?.address     || null) : null,
        supplierGoogleLink: hasSupplier ? (sd?.googleLink  || null) : null,
        requiresApproval:   hasSupplier && !form.overrideApproved,
        sendEmail:          hasSupplier && !form.overrideApproved ? form.sendEmail : false,
        approvalStatus,
        notes: form.notes.trim() || null,
        transferMode:       isTransfer ? form.transferMode : null,
        pickupLocation:     isTransfer ? (form.pickupLocation.trim() || null) : null,
        pickupAddress:      isTransfer ? (form.pickupAddress.trim() || null) : null,
        pickupGoogleLink:   isTransfer ? (form.pickupGoogleLink.trim() || null) : null,
        dropoffLocation:    isTransfer ? (form.dropoffLocation.trim() || null) : null,
        dropoffAddress:     isTransfer ? (form.dropoffAddress.trim() || null) : null,
        dropoffGoogleLink:  isTransfer ? (form.dropoffGoogleLink.trim() || null) : null,
        fixtureIds:         isFixturesActivity(form.activityType) && form.fixtureIds.length ? form.fixtureIds : null,
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save.');
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
      style={{ background: 'rgba(13,34,64,0.4)', backdropFilter: 'blur(3px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-[16px] w-full max-w-[500px] max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E7EB]">
          <div>
            <h3 className="text-[15px] font-bold text-navy">{isEdit ? 'Edit Activity' : 'Add Activity'}</h3>
            <p className="text-[12px] text-[#9CA3AF] mt-0.5">Day {dayIdx + 1} — {dayLabel(request.departureDate, dayIdx)}</p>
          </div>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-dr text-2xl w-8 h-8 flex items-center justify-center leading-none">×</button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {item?.needsDetails && (
            <p className="text-[12px] text-[#78350F] bg-[#FFFBEB] border border-amber-200 rounded-[8px] px-3 py-2">
              Copied from the skeletal: <strong>{item.title}</strong>. The start time is only a suggestion, so check it and choose the activity. Saving marks this as done.
            </p>
          )}

          {/* Times */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-[#6B7280] mb-1">Start time *</label>
              <input type="time" value={form.time} onChange={(e) => set('time', e.target.value)} required
                className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-[#6B7280] mb-1">End time</label>
              <input type="time" value={form.endTime} onChange={(e) => set('endTime', e.target.value)}
                className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
            </div>
          </div>

          {/* Activity type */}
          <div>
            <label className="block text-[11px] font-semibold text-[#6B7280] mb-1">Activity *</label>
            {categories.length === 0 ? (
              <p className="text-[12px] text-[#9CA3AF]">
                No activities configured — <a href="/staff/admin-settings" className="text-pb hover:underline">add them in Admin Settings</a>.
              </p>
            ) : (
              <select
                value={form.activityType}
                onChange={(e) => handleActivityTypeChange(e.target.value)}
                required
                className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white"
              >
                <option value="">Select activity…</option>
                {[...categories]
                  .sort((a, b) => displayActivityType(a).localeCompare(displayActivityType(b)))
                  .map((c) => <option key={c} value={c}>{displayActivityType(c)}</option>)}
              </select>
            )}
          </div>

          {/* Supplier picker */}
          <div>
            <label className="block text-[11px] font-semibold text-[#6B7280] mb-1">
              Supplier <span className="text-[#9CA3AF] font-normal">(optional)</span>
            </label>
            {form.supplierId && form.supplierData ? (
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-[#F0FDF4] border border-[#86EFAC] rounded-[8px] px-3 py-2">
                  <p className="text-[13px] font-semibold text-[#15803D]">{form.supplierData.name}</p>
                  {form.supplierData.email && <p className="text-[11px] text-[#16A34A]">{form.supplierData.email}</p>}
                </div>
                <button type="button" onClick={handleClearSupplier}
                  className="text-[#9CA3AF] hover:text-dr text-xl w-8 h-8 flex items-center justify-center">×</button>
              </div>
            ) : (
              <SupplierSearchInput
                suppliers={allSuppliers}
                selectedId={form.supplierId}
                onSelect={handleSupplierSelect}
                filterByType={form.activityType || null}
                placeholder={form.activityType ? `Search ${form.activityType} suppliers…` : 'Select an activity first…'}
                bookingStatusMap={bookingStatusMap}
              />
            )}
            {form.supplierData && (() => {
              const bStatus = bookingStatusMap[form.supplierData.id];
              const bBadge = bStatus ? BOOKING_STATUS_BADGE[bStatus] : null;
              return (
                <div className="mt-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-[10px] p-3 space-y-1">
                  {bBadge && (
                    <div style={{ background: bBadge.bg, borderColor: bBadge.dot + '55' }} className="flex items-center gap-1.5 px-2 py-1.5 rounded-[6px] border mb-2">
                      <span style={{ background: bBadge.dot }} className="w-2 h-2 rounded-full flex-shrink-0" />
                      <p style={{ color: bBadge.color }} className="text-[11px] font-semibold">{bBadge.label}</p>
                    </div>
                  )}
                  <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-1">Contact details</p>
                  {form.supplierData.address    && <p className="text-[12px] text-[#374151]">📍 {form.supplierData.address}</p>}
                  {form.supplierData.phone      && <p className="text-[12px] text-[#374151]">📞 {form.supplierData.phone}</p>}
                  {form.supplierData.email      && <p className="text-[12px] text-[#374151]">✉ {form.supplierData.email}</p>}
                  {form.supplierData.googleLink && (
                    <a href={form.supplierData.googleLink} target="_blank" rel="noopener noreferrer"
                      className="text-[12px] text-pb hover:underline">🗺 Google Maps ↗</a>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Travel/Transfer-specific fields */}
          {isTransferLike(form.activityType) && (
            <div className="space-y-4 p-4 bg-[#F0F7FF] border border-[#BFDBFE] rounded-[12px]">
              <p className="text-[11px] font-bold text-[#1D4ED8] uppercase tracking-wider">Travel details</p>

              {/* Transport mode */}
              <div>
                <label className="block text-[11px] font-semibold text-[#6B7280] mb-2">Transport mode</label>
                <div className="flex gap-2">
                  {['bus', 'tram', 'walk'].map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => set('transferMode', mode)}
                      className={`flex-1 py-2 text-[12px] font-semibold rounded-[8px] border transition capitalize ${
                        form.transferMode === mode
                          ? 'bg-pb text-white border-pb'
                          : 'bg-white text-[#374151] border-[#E5E7EB] hover:border-pb hover:text-pb'
                      }`}
                    >
                      {mode === 'bus' ? '🚌 Bus' : mode === 'tram' ? '🚊 Tram' : '🚶 Walk'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Pick-up */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] font-bold text-[#374151] uppercase tracking-wider">Pick-up</p>
                  {form.pickupLocation.trim() && (
                    <button type="button"
                      onClick={() => saveToLibrary(form.pickupLocation, form.pickupAddress, form.pickupGoogleLink)}
                      className="text-[10px] font-semibold text-pb hover:text-navy transition">
                      {libMsg === 'saved' ? 'Saved to library ✓' : libMsg === 'saving' ? 'Saving…' : libMsg === 'error' ? 'Could not save' : '+ Save to library'}
                    </button>
                  )}
                </div>
                <LocationSearchInput locations={allLocations}
                  onSelect={(loc) => applySavedLocation(loc, { name: 'pickupLocation', address: 'pickupAddress', google: 'pickupGoogleLink' })} />
                <input type="text" value={form.pickupLocation}
                  onChange={(e) => set('pickupLocation', e.target.value)}
                  placeholder="Location name (e.g. Hotel reception)"
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white" />
                <input type="text" value={form.pickupAddress}
                  onChange={(e) => set('pickupAddress', e.target.value)}
                  placeholder="Full address"
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white" />
                <input type="url" value={form.pickupGoogleLink}
                  onChange={(e) => set('pickupGoogleLink', e.target.value)}
                  placeholder="Google Maps link (optional)"
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white" />
              </div>

              {/* Drop-off */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] font-bold text-[#374151] uppercase tracking-wider">Drop-off</p>
                  {form.dropoffLocation.trim() && (
                    <button type="button"
                      onClick={() => saveToLibrary(form.dropoffLocation, form.dropoffAddress, form.dropoffGoogleLink)}
                      className="text-[10px] font-semibold text-pb hover:text-navy transition">
                      {libMsg === 'saved' ? 'Saved to library ✓' : libMsg === 'saving' ? 'Saving…' : libMsg === 'error' ? 'Could not save' : '+ Save to library'}
                    </button>
                  )}
                </div>
                <LocationSearchInput locations={allLocations}
                  onSelect={(loc) => applySavedLocation(loc, { name: 'dropoffLocation', address: 'dropoffAddress', google: 'dropoffGoogleLink' })} />
                <input type="text" value={form.dropoffLocation}
                  onChange={(e) => set('dropoffLocation', e.target.value)}
                  placeholder="Location name (e.g. Stadium entrance)"
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white" />
                <input type="text" value={form.dropoffAddress}
                  onChange={(e) => set('dropoffAddress', e.target.value)}
                  placeholder="Full address"
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white" />
                <input type="url" value={form.dropoffGoogleLink}
                  onChange={(e) => set('dropoffGoogleLink', e.target.value)}
                  placeholder="Google Maps link (optional)"
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white" />
              </div>
            </div>
          )}

          {/* Location */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-[11px] font-semibold text-[#6B7280]">
                Location {form.supplierId && <span className="text-[#9CA3AF] font-normal">(manual override)</span>}
              </label>
              {locationEdited && form.supplierData?.locationName ? (
                <button type="button" onClick={resetLocationToSupplier}
                  className="text-[10px] text-pb hover:text-navy font-medium transition">
                  Reset to supplier default
                </button>
              ) : (!form.supplierId && form.locationName.trim() && (
                <button type="button"
                  onClick={() => saveToLibrary(form.locationName, form.locationAddress, form.locationGoogleLink)}
                  className="text-[10px] font-semibold text-pb hover:text-navy transition">
                  {libMsg === 'saved' ? 'Saved to library ✓' : libMsg === 'saving' ? 'Saving…' : libMsg === 'error' ? 'Could not save' : '+ Save to library'}
                </button>
              ))}
            </div>
            <div className="mb-2">
              <LocationSearchInput locations={allLocations}
                onSelect={(loc) => applySavedLocation(loc, { name: 'locationName', address: 'locationAddress', google: 'locationGoogleLink' })} />
            </div>
            <input
              type="text"
              value={form.locationName}
              onChange={(e) => { set('locationName', e.target.value); setLocationEdited(true); }}
              placeholder="e.g. Elche CF Training Ground, Valencia CF Stadium"
              className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
            />
            <div className="space-y-2 mt-2">
              <input type="text" value={form.locationAddress}
                onChange={(e) => set('locationAddress', e.target.value)}
                placeholder={form.supplierId
                  ? "Address of this place (leave blank to use the supplier's address)"
                  : 'Full address (shows under the location on the itinerary)'}
                className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
              <input type="url" value={form.locationGoogleLink}
                onChange={(e) => set('locationGoogleLink', e.target.value)}
                placeholder="Google Maps link (optional)"
                className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
            </div>
            {form.supplierId && (
              <p className="text-[10px] text-[#9CA3AF] mt-1">Auto-fills from supplier — edit here to override for this activity only.</p>
            )}
          </div>

          {/* Matches (Fixtures activity) */}
          {isFixturesActivity(form.activityType) && tour && (
            <MatchesPicker
              dayDate={itineraryDayDate(request.departureDate, dayIdx)}
              item={{ day: dayIdx + 1, time: form.time, endTime: form.endTime }}
              departureDate={request.departureDate}
              selected={form.fixtureIds}
              onChange={(ids) => set('fixtureIds', ids)}
              tour={tour}
              onEdit={(f) => setFixtureModal({ fixture: f })}
              onNew={() => setFixtureModal({ defaults: { date: itineraryDayDate(request.departureDate, dayIdx), time: form.time, endTime: form.endTime } })}
            />
          )}

          {/* Notes */}
          <div>
            <label className="block text-[11px] font-semibold text-[#6B7280] mb-1">
              Notes <span className="text-[#9CA3AF] font-normal">(optional)</span>
            </label>
            <input type="text" value={form.notes} onChange={(e) => set('notes', e.target.value)}
              placeholder="Any additional information…"
              className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
          </div>

          {/* Approval (only when supplier assigned) */}
          {form.supplierId && (
            <div className="space-y-3 p-3 bg-[#F9FAFB] rounded-[10px] border border-[#E5E7EB]">
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input type="checkbox" checked={form.overrideApproved} onChange={(e) => set('overrideApproved', e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-[#D1D5DB] text-sg focus:ring-sg" />
                <div>
                  <p className="text-[12px] font-semibold text-[#374151]">Auto-approve this activity</p>
                  <p className="text-[11px] text-[#9CA3AF]">Mark as confirmed immediately — no approval email sent</p>
                </div>
              </label>
              {!form.overrideApproved && (
                <div className="flex items-center justify-between gap-3 pt-2 border-t border-[#E5E7EB]">
                  <div>
                    <p className="text-[12px] font-semibold text-[#374151]">Send approval email?</p>
                    <p className="text-[11px] text-[#9CA3AF]">Turn off if already confirmed by phone</p>
                  </div>
                  <Toggle on={form.sendEmail} onChange={(v) => set('sendEmail', v)} />
                </div>
              )}
            </div>
          )}

          {error && <p className="text-[12px] text-dr">{error}</p>}

          <div className="flex gap-2 pt-1">
            <button type="submit" disabled={saving}
              className="flex-1 py-2.5 bg-pb hover:bg-navy text-white text-[13px] font-bold rounded-[9px] transition disabled:opacity-60">
              {saving ? 'Saving…' : (isEdit ? 'Update Activity' : 'Add Activity')}
            </button>
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-[13px] text-[#6B7280] hover:text-[#374151]">
              Cancel
            </button>
          </div>
        </form>
      </div>
      {fixtureModal && tour && (
        <FixtureFormModal
          requestId={tour.requestId}
          fixture={fixtureModal.fixture || null}
          defaults={fixtureModal.defaults || {}}
          teams={tour.teams}
          canSeeContacts
          contacts={fixtureModal.fixture ? fixtureContacts[fixtureModal.fixture.id] : null}
          onSaved={(id) => setForm((p) => (p.fixtureIds.includes(id) ? p : { ...p, fixtureIds: [...p.fixtureIds, id] }))}
          onClose={() => setFixtureModal(null)}
        />
      )}
    </div>
  );
}

// ── Matches picker (Fixtures activity) ───────────────────────────────────────
// Ticks which of the tour's fixtures this activity covers. The fixtures live
// on the Team Info tab; adding or editing one here changes the same record.

function MatchesPicker({ dayDate, item, departureDate, selected, onChange, tour, onEdit, onNew }) {
  const { fixtures, teamsById } = tour;
  const [showOtherDays, setShowOtherDays] = useState(false);
  const sorted = sortFixtures(fixtures, teamsById);
  const sameDay = sorted.filter((f) => f.date === dayDate || selected.includes(f.id));
  const otherDays = sorted.filter((f) => !sameDay.includes(f));
  const missing = selected.filter((id) => !fixtures.some((f) => f.id === id));
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  const row = (f) => (
    <label key={f.id} className="flex items-start gap-2 cursor-pointer">
      <input type="checkbox" checked={selected.includes(f.id)} onChange={() => toggle(f.id)}
        className="mt-2 w-4 h-4 rounded border-[#D1D5DB] text-pb focus:ring-pb flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <MatchLine
          fixture={f}
          team={teamsById[f.teamId]}
          size="sm"
          showDate={f.date !== dayDate}
          warning={selected.includes(f.id) ? fixtureMismatch(f, item, departureDate) : ''}
          onEdit={onEdit}
        />
      </div>
    </label>
  );

  return (
    <div className="p-3 bg-[#F9FAFB] rounded-[10px] border border-[#E5E7EB] space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-[12px] font-bold text-[#374151]">
          Matches {selected.length > 0 && <span className="text-pb">· {selected.length - missing.length} selected</span>}
        </p>
        <button type="button" onClick={onNew} disabled={tour.teams.length === 0}
          className="text-[11.5px] font-semibold text-pb hover:text-navy disabled:text-[#D1D5DB]">
          + New fixture
        </button>
      </div>
      {tour.teams.length === 0 ? (
        <p className="text-[11.5px] text-[#9CA3AF]">This tour has no teams yet. They come from the squads on the Team Info form, or the Teams tab.</p>
      ) : sameDay.length === 0 ? (
        <p className="text-[11.5px] text-[#9CA3AF]">No fixtures on this day yet. Add one, and it will also show on the Team Info tab.</p>
      ) : (
        <div className="space-y-1.5">{sameDay.map(row)}</div>
      )}
      {missing.length > 0 && (
        <div className="flex items-center justify-between gap-2">
          <p className="text-[11.5px] font-semibold text-dr">{missing.length} fixture{missing.length !== 1 ? 's' : ''} removed from Team Info.</p>
          <button type="button" onClick={() => onChange(selected.filter((id) => !missing.includes(id)))}
            className="text-[11.5px] font-semibold text-dr hover:underline">Take off this activity</button>
        </div>
      )}
      {otherDays.length > 0 && (
        <div>
          <button type="button" onClick={() => setShowOtherDays((v) => !v)} className="text-[11px] font-semibold text-[#6B7280] hover:text-pb">
            {showOtherDays ? 'Hide' : 'Show'} fixtures on other days ({otherDays.length})
          </button>
          {showOtherDays && <div className="space-y-1.5 mt-1.5">{otherDays.map(row)}</div>}
        </div>
      )}
    </div>
  );
}

// ── Question modal ───────────────────────────────────────────────────────────

function QuestionModal({ item, requestId, onClose }) {
  const [response, setResponse] = useState('');
  const [saving, setSaving]     = useState(false);

  async function handleSave() {
    if (!response.trim()) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'requests', requestId, 'itinerary', item.id), {
        mstResponse: response.trim(),
        approvalStatus: 'pending',
        questionAnsweredAt: serverTimestamp(),
      });
      onClose();
    } catch {
      alert('Failed to save response.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(13,34,64,0.4)', backdropFilter: 'blur(3px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-[16px] w-full max-w-[440px] shadow-2xl p-6">
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-[15px] font-bold text-navy">Supplier Question</h3>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-dr text-2xl leading-none">×</button>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-[10px] p-3 mb-4">
          <p className="text-[10px] font-bold text-[#92400E] uppercase tracking-wider mb-1">
            Question from {item.supplierName}
          </p>
          <p className="text-[13px] text-[#78350F]">{item.questionText}</p>
        </div>
        <label className="block text-[11px] font-semibold text-[#6B7280] mb-1.5">Your response</label>
        <textarea value={response} onChange={(e) => setResponse(e.target.value)} rows={3}
          placeholder="Type your reply…"
          className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb resize-none mb-3" />
        <p className="text-[11px] text-[#9CA3AF] mb-4">
          After saving, re-confirm the itinerary to send the response and a new approval request to the supplier.
        </p>
        <div className="flex gap-2">
          <button onClick={handleSave} disabled={!response.trim() || saving}
            className="flex-1 py-2.5 bg-pb text-white text-[13px] font-bold rounded-[9px] transition disabled:opacity-60">
            {saving ? 'Saving…' : 'Save Response'}
          </button>
          <button onClick={onClose} className="px-5 text-[13px] text-[#6B7280] hover:text-[#374151]">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Supplier detail modal (tour leader taps a row) ───────────────────────────

const TRANSFER_MODE_LABEL = { bus: '🚌 Bus', tram: '🚊 Tram', walk: '🚶 Walk' };

function TransferLocationCard({ heading, location, address, googleLink }) {
  const t = useT();
  if (!location && !address && !googleLink) return null;
  return (
    <div className="rounded-[10px] border border-[#E5E7EB] overflow-hidden">
      <div className="bg-[#F9FAFB] px-3 py-1.5">
        <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">{heading}</p>
      </div>
      <div className="px-3 py-2.5 space-y-1.5">
        {location && <p className="text-[13px] font-semibold text-[#111827]">{location}</p>}
        {address && (
          <div className="flex items-start gap-2">
            <span className="text-sm shrink-0 mt-0.5">📍</span>
            <p className="text-[12px] text-[#374151] leading-snug">{address}</p>
          </div>
        )}
        {googleLink && (
          <a href={googleLink} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 text-[12px] font-semibold text-pb hover:text-navy transition">
            <span>🗺</span> {t('itinerary.openMaps')}
          </a>
        )}
      </div>
    </div>
  );
}

function SupplierDetailModal({ item, onClose }) {
  const t = useT();
  const isTransfer = item.transferMode != null;
  const hasSupplierDetails = item.supplierAddress || item.supplierPhone || item.supplierEmail || item.supplierGoogleLink;
  const hasTransferDetails = item.pickupLocation || item.pickupAddress || item.dropoffLocation || item.dropoffAddress;
  const noDetails = !hasSupplierDetails && !item.notes && !hasTransferDetails;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(13,34,64,0.4)', backdropFilter: 'blur(3px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-[16px] w-full max-w-[400px] shadow-2xl overflow-hidden">
        <div className="bg-navy px-6 py-4">
          <p className="text-[11px] font-bold text-blue-300 uppercase tracking-wider mb-0.5">
            {activityLabel(item.activityType, t) || t('itinerary.activity')} · {item.time}{item.endTime ? ` – ${item.endTime}` : ''}
          </p>
          <h3 className="text-[15px] font-bold text-white">
            {item.locationName || t.value('title', item.title) || item.supplierName || t('itinerary.activity')}
          </h3>
          {item.supplierName && item.supplierName !== (item.locationName || item.title) && (
            <p className="text-[12px] text-blue-200 mt-0.5">{item.supplierName}</p>
          )}
          {isTransfer && item.transferMode && (
            <span className="inline-block mt-1.5 text-[11px] font-bold bg-white/20 text-white px-2 py-0.5 rounded-full">
              {TRANSFER_MODE_LABEL[item.transferMode] ? t(`itinerary.transfer.${item.transferMode}`) : item.transferMode}
            </span>
          )}
        </div>
        <div className="p-5 space-y-3 max-h-[60vh] overflow-y-auto">
          {noDetails && (
            <p className="text-[13px] text-[#9CA3AF]">{t('itinerary.noDetails')}</p>
          )}

          {/* Transfer pick-up / drop-off */}
          {isTransfer && (
            <>
              <TransferLocationCard
                heading={t('itinerary.pickup')}
                location={item.pickupLocation}
                address={item.pickupAddress}
                googleLink={item.pickupGoogleLink}
              />
              <TransferLocationCard
                heading={t('itinerary.dropoff')}
                location={item.dropoffLocation}
                address={item.dropoffAddress}
                googleLink={item.dropoffGoogleLink}
              />
            </>
          )}

          {/* Supplier contact details */}
          {hasSupplierDetails && (
            <div className={`space-y-3 ${isTransfer ? 'pt-3 border-t border-[#F3F4F6]' : ''}`}>
              {isTransfer && <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">{t('itinerary.supplierContact')}</p>}
              {item.supplierAddress && (
                <div className="flex items-start gap-2.5">
                  <span className="text-lg shrink-0">📍</span>
                  <p className="text-[13px] text-[#374151] leading-snug">{item.supplierAddress}</p>
                </div>
              )}
              {item.supplierPhone && (
                <div className="flex items-center gap-2.5">
                  <span className="text-lg shrink-0">📞</span>
                  <a href={`tel:${item.supplierPhone}`} className="text-[13px] font-semibold text-pb hover:underline">
                    {item.supplierPhone}
                  </a>
                </div>
              )}
              {item.supplierEmail && (
                <div className="flex items-center gap-2.5">
                  <span className="text-lg shrink-0">✉️</span>
                  <a href={`mailto:${item.supplierEmail}`} className="text-[13px] text-pb hover:underline">
                    {item.supplierEmail}
                  </a>
                </div>
              )}
              {item.supplierGoogleLink && (
                <a href={item.supplierGoogleLink} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2.5 p-3 bg-blue-light rounded-[10px] hover:bg-blue-light/70 transition">
                  <span className="text-lg">🗺</span>
                  <span className="text-[13px] font-semibold text-pb">{t('itinerary.openMaps')}</span>
                </a>
              )}
            </div>
          )}

          {item.notes && (
            <div className="flex items-start gap-2.5 border-t border-[#F3F4F6] pt-3">
              <span className="text-lg shrink-0">📝</span>
              <p className="text-[13px] text-[#374151] leading-snug">{item.notes}</p>
            </div>
          )}
        </div>
        <div className="px-5 pb-5">
          <button onClick={onClose}
            className="w-full py-2.5 bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#374151] text-[13px] font-semibold rounded-[10px] transition">
            {t('itinerary.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Confirm itinerary modal ───────────────────────────────────────────────────

function ConfirmItineraryModal({ suppliersToEmail, confirming, onConfirm, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(13,34,64,0.4)', backdropFilter: 'blur(3px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-[16px] w-full max-w-[480px] shadow-2xl p-6">
        <div className="flex items-start justify-between mb-5">
          <div>
            <h3 className="text-[16px] font-bold text-navy">Confirm Itinerary</h3>
            <p className="text-[12px] text-[#6B7280] mt-1">
              {suppliersToEmail.length > 0
                ? `Approval emails will be sent to ${suppliersToEmail.length} supplier${suppliersToEmail.length > 1 ? 's' : ''}`
                : 'No approval emails will be sent'}
            </p>
          </div>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-dr text-2xl leading-none w-8 h-8 flex items-center justify-center">×</button>
        </div>

        {suppliersToEmail.length > 0 && (
          <div className="space-y-2 mb-5 max-h-52 overflow-y-auto">
            {suppliersToEmail.map((s) => (
              <div key={s.supplierId} className="flex items-center justify-between p-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-[10px]">
                <div>
                  <p className="text-[13px] font-semibold text-[#111827]">{s.supplierName}</p>
                  <p className="text-[11px] text-[#9CA3AF]">{s.supplierEmail}</p>
                </div>
                <span className="text-[11px] font-bold text-pb bg-blue-light px-2 py-0.5 rounded-full shrink-0">
                  {s.items.length} item{s.items.length > 1 ? 's' : ''}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="bg-amber-50 border border-amber-200 rounded-[10px] p-3 mb-5">
          <p className="text-[12px] text-[#92400E]">
            ⚠️ Emails cannot be recalled once sent. Confirm all items are correct before proceeding.
          </p>
        </div>

        <div className="flex gap-3">
          <button onClick={onConfirm} disabled={confirming}
            className="flex-1 py-2.5 bg-navy hover:bg-pb text-white text-[13px] font-bold rounded-[9px] transition disabled:opacity-60">
            {confirming ? 'Sending…' : (suppliersToEmail.length > 0
              ? `Confirm & Send ${suppliersToEmail.length} Email${suppliersToEmail.length > 1 ? 's' : ''}`
              : 'Confirm Itinerary')}
          </button>
          <button onClick={onClose} className="px-5 text-[13px] text-[#6B7280] hover:text-[#374151]">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Itinerary table row ───────────────────────────────────────────────────────

function ItineraryRow({ item, readOnly, canUploadTickets, onEdit, onDelete, onOverride, onViewQuestion, onDetail, onLocationSave, onLeaderNotesSave, onUploadTicket, onRemoveTicket, isUploadingTicket, bookingStatusMap = {}, matches = null }) {
  const t = useT();
  const st    = STATUS[item.approvalStatus] || STATUS.pending;
  const stKey = STATUS[item.approvalStatus] ? item.approvalStatus : 'pending';
  const green = isGreen(item.approvalStatus);
  const hasDetails = item.supplierAddress || item.supplierPhone || item.supplierEmail || item.supplierGoogleLink || item.notes
    || item.pickupLocation || item.pickupAddress || item.dropoffLocation || item.dropoffAddress;

  const locationDisplay = item.locationName
    || (item.itemType === 'meal' ? item.supplierName : null)
    || null;

  const [editingLoc, setEditingLoc] = useState(false);
  const [locDraft,   setLocDraft]   = useState('');

  const canInlineEditLocation = !readOnly && !item.transferMode;

  function startLocationEdit(e) {
    e.stopPropagation();
    setLocDraft(locationDisplay || '');
    setEditingLoc(true);
  }

  function commitLocationEdit() {
    setEditingLoc(false);
    const trimmed = locDraft.trim();
    if (trimmed !== (locationDisplay || '')) {
      onLocationSave(item.id, trimmed || null);
    }
  }

  function handleLocationKeyDown(e) {
    if (e.key === 'Enter') { e.preventDefault(); commitLocationEdit(); }
    if (e.key === 'Escape') { setEditingLoc(false); }
  }

  // Notes for the tour leaders — staff-only edits, kept apart from `notes`,
  // which goes to suppliers.
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft,   setNotesDraft]   = useState('');

  function startNotesEdit(e) {
    e.stopPropagation();
    setNotesDraft(item.leaderNotes || '');
    setEditingNotes(true);
  }

  function commitNotesEdit() {
    setEditingNotes(false);
    const trimmed = notesDraft.trim();
    if (trimmed !== (item.leaderNotes || '')) onLeaderNotesSave(item.id, trimmed || null);
  }

  const hasMatches = isFixturesActivity(item.activityType) && (item.fixtureIds || []).length > 0 && matches;

  return (
    <>
    <tr
      className={`${hasMatches ? '' : 'border-b border-[#F3F4F6]'} group transition-colors ${hasDetails ? 'cursor-pointer hover:bg-blue-light/20' : 'hover:bg-[#FAFAFA]'}`}
      onClick={hasDetails ? () => onDetail(item) : undefined}
    >
      {/* Time */}
      <td className={`px-4 py-3 text-[12px] tabular-nums whitespace-nowrap align-top ${item.needsDetails ? 'font-medium italic text-[#9CA3AF]' : 'font-bold text-[#374151]'}`}>
        {item.time || '—'}
        {item.endTime && <span className="block text-[10px] text-[#9CA3AF] font-normal">{item.endTime}</span>}
      </td>

      {/* Activity */}
      <td className="px-4 py-3 align-top">
        <span className="text-[12px] font-semibold text-[#374151]">
          {(item.needsDetails && item.title) || activityLabel(item.activityType, t) || t.value('title', item.title) || '—'}
        </span>
        {item.transferMode && (
          <span className="block text-[10px] font-semibold text-pb mt-0.5">
            {TRANSFER_MODE_LABEL[item.transferMode] ? t(`itinerary.transfer.${item.transferMode}`) : item.transferMode}
          </span>
        )}
      </td>

      {/* Notes for tour leaders */}
      <td
        className="px-4 py-3 align-top min-w-[160px] max-w-[260px]"
        onClick={!readOnly && !editingNotes ? startNotesEdit : (e) => e.stopPropagation()}
      >
        {editingNotes ? (
          <textarea
            value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
            onBlur={commitNotesEdit}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commitNotesEdit(); }
              if (e.key === 'Escape') setEditingNotes(false);
            }}
            onClick={(e) => e.stopPropagation()}
            autoFocus
            rows={2}
            className="w-full border border-pb rounded-[6px] px-2 py-1 text-[12px] resize-y focus:outline-none focus:ring-2 focus:ring-pb"
            placeholder="Notes for the tour leaders…"
          />
        ) : (
          <span className={`block text-[12px] text-[#374151] leading-snug whitespace-pre-line ${!readOnly ? 'hover:text-pb cursor-text group/notes' : ''}`}>
            {item.leaderNotes || (
              readOnly
                ? <span className="text-[#9CA3AF]">—</span>
                : <span className="text-[#C4C9D1] opacity-0 group-hover:opacity-100 transition-opacity">+ Add note</span>
            )}
            {!readOnly && item.leaderNotes && (
              <span className="opacity-0 group-hover/notes:opacity-100 ml-1.5 text-[10px] text-[#9CA3AF] transition-opacity">✎</span>
            )}
          </span>
        )}
      </td>

      {/* Location */}
      <td
        className="px-4 py-3 align-top"
        onClick={canInlineEditLocation && !editingLoc ? startLocationEdit : (e) => e.stopPropagation()}
      >
        {item.transferMode ? (
          <span className="text-[12px] text-[#374151] leading-snug">
            {item.pickupLocation && <span className="block">↑ {item.pickupLocation}</span>}
            {item.dropoffLocation && <span className="block">↓ {item.dropoffLocation}</span>}
            {!item.pickupLocation && !item.dropoffLocation && <span className="text-[#9CA3AF]">—</span>}
          </span>
        ) : editingLoc ? (
          <input
            type="text"
            value={locDraft}
            onChange={(e) => setLocDraft(e.target.value)}
            onBlur={commitLocationEdit}
            onKeyDown={handleLocationKeyDown}
            onClick={(e) => e.stopPropagation()}
            autoFocus
            className="w-full border border-pb rounded-[6px] px-2 py-1 text-[12px] focus:outline-none focus:ring-2 focus:ring-pb"
            placeholder="Enter location…"
          />
        ) : (
          <span className={`text-[12px] text-[#374151] ${canInlineEditLocation ? 'hover:text-pb cursor-text group/loc relative' : ''}`}>
            {locationDisplay || <span className="text-[#9CA3AF]">—</span>}
            {canInlineEditLocation && (
              <span className="opacity-0 group-hover/loc:opacity-100 ml-1.5 text-[10px] text-[#9CA3AF] transition-opacity">✎</span>
            )}
          </span>
        )}
      </td>

      {/* Supplier */}
      <td className="px-4 py-3 align-top">
        {item.supplierName ? (
          <>
            <span className="text-[12px] text-[#374151]">{item.supplierName}</span>
            {item.supplierId && bookingStatusMap[item.supplierId] && (() => {
              const b = BOOKING_STATUS_BADGE[bookingStatusMap[item.supplierId]];
              const bLabel = b ? t(`itinerary.booking.${bookingStatusMap[item.supplierId]}`) : '';
              return b ? (
                <span title={bLabel} style={{ background: b.bg, color: b.color }} className="inline-flex items-center gap-1 ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full">
                  <span style={{ background: b.dot }} className="w-1.5 h-1.5 rounded-full inline-block flex-shrink-0" />
                  {bLabel}
                </span>
              ) : null;
            })()}
          </>
        ) : (
          <span className="text-[#9CA3AF]">—</span>
        )}
        {item.notes && <p className="text-[11px] text-[#9CA3AF] italic mt-0.5">{item.notes}</p>}
      </td>

      {/* Status */}
      <td className="px-4 py-3 align-top">
        {item.needsDetails ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap bg-[#FFFBEB] border border-[#FCD34D] text-[#D97706]">
            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: NEEDS_DETAILS_DOT }} />
            {t('itinerary.needsDetails')}
          </span>
        ) : item.approvalStatus === 'question' && item.questionText ? (
          <button
            onClick={(e) => { e.stopPropagation(); onViewQuestion(item); }}
            className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#D97706] bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-[6px] hover:bg-amber-100 transition"
          >
            {t('itinerary.question')}
          </button>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap"
            style={{ background: st.bg, border: `1px solid ${st.border}`, color: st.text }}>
            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: st.dot }} />
            {t(`itinerary.status.${stKey}`)}
          </span>
        )}
      </td>

      {/* Ticket */}
      <td className="px-3 py-3 align-top">
        {item.ticketUrl ? (
          <div className="flex flex-col gap-1">
            <a
              href={item.ticketUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              title="Download ticket"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-[6px] text-[10px] font-semibold bg-green-light text-sg border border-green-200 hover:bg-green-100 transition whitespace-nowrap"
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              {t('itinerary.ticket')}
            </a>
            {(!readOnly || canUploadTickets) && (
              <div className="flex items-center gap-1">
                <button
                  onClick={(e) => { e.stopPropagation(); onUploadTicket(item.id); }}
                  disabled={isUploadingTicket}
                  title="Replace ticket"
                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-[5px] text-[10px] font-semibold text-[#6B7280] border border-[#D1D5DB] hover:text-pb hover:border-pb transition disabled:opacity-40"
                >
                  ↑ Replace
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onRemoveTicket(item.id, item.ticketStoragePath); }}
                  title="Remove ticket"
                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-[5px] text-[10px] font-semibold text-dr border border-[#FCA5A5] hover:bg-[#FEF2F2] transition"
                >
                  ✕ Remove
                </button>
              </div>
            )}
          </div>
        ) : (!readOnly || canUploadTickets) ? (
          <button
            onClick={(e) => { e.stopPropagation(); onUploadTicket(item.id); }}
            disabled={isUploadingTicket}
            title="Upload ticket PDF"
            className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1 px-2 py-0.5 rounded-[6px] text-[10px] font-semibold text-[#9CA3AF] border border-[#E5E7EB] hover:text-pb hover:border-pb disabled:opacity-40"
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>
            {isUploadingTicket ? '…' : 'Upload'}
          </button>
        ) : null}
      </td>

      {/* Actions (staff only) */}
      {!readOnly && (
        <td className="px-3 py-3 align-top">
          <div className={`flex items-center gap-0.5 ${item.itemType === 'meal' ? '' : 'opacity-0 group-hover:opacity-100'} transition-opacity justify-end`}>
            {!green && (
              <button onClick={(e) => { e.stopPropagation(); onOverride(item); }} title="Mark as confirmed"
                className="w-6 h-6 flex items-center justify-center text-sg hover:bg-green-100 rounded-[4px] transition">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </button>
            )}
            <button onClick={(e) => { e.stopPropagation(); onEdit(item); }} title="Edit"
              className="w-6 h-6 flex items-center justify-center text-[#9CA3AF] hover:text-pb rounded-[4px] transition">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
            </button>
            <button onClick={(e) => { e.stopPropagation(); onDelete(item.id); }} title="Remove"
              className="w-6 h-6 flex items-center justify-center text-[#D1D5DB] hover:text-dr rounded-[4px] transition">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </td>
      )}
    </tr>
    {hasMatches && (
      <tr className="border-b border-[#F3F4F6]">
        <td />
        <td colSpan={readOnly ? 6 : 7} className="px-4 pb-3">
          <ActivityMatches
            item={item}
            fixturesById={matches.fixturesById}
            teamsById={matches.teamsById}
            contactsById={matches.contactsById}
            myLeaderId={matches.myLeaderId}
            departureDate={matches.departureDate}
            showRemoved={!readOnly}
            showWarnings={!readOnly}
            size="sm"
          />
        </td>
      </tr>
    )}
    </>
  );
}

// ── Main export ──────────────────────────────────────────────────────────────

// `restricted` skips the supplier-related subscriptions — collections that
// marketing-only accounts cannot read under the Firestore rules.
// `showFixtureContacts` shows the opposition coach / referee on each match
// (staff and tour leaders); `myLeaderId` highlights that tour leader's team.
export default function FullItineraryBuilder({ requestId, request, readOnly = false, canUploadTickets = false, restricted = false, showFixtureContacts = !readOnly && !restricted, myLeaderId = null }) {
  const t = useT();
  const nDays = numDays(request.departureDate, request.returnDate);
  const tourTeams = useTourTeams(requestId, request, { sync: !restricted });
  const fixtureContacts = useFixtureContacts(requestId, showFixtureContacts);
  const fixturesById = useMemo(
    () => Object.fromEntries(tourTeams.fixtures.map((f) => [f.id, f])),
    [tourTeams.fixtures]
  );
  const matches = useMemo(() => ({
    fixturesById,
    teamsById: tourTeams.teamsById,
    contactsById: showFixtureContacts ? fixtureContacts : null,
    myLeaderId,
    departureDate: request.departureDate,
  }), [fixturesById, tourTeams.teamsById, showFixtureContacts, fixtureContacts, myLeaderId, request.departureDate]);
  const days  = useMemo(() => Array.from({ length: nDays }, (_, i) => i), [nDays]);

  const [items,        setItems]       = useState([]);
  const [fullMeta,     setFullMeta]    = useState({ status: 'draft' });
  const [allSuppliers, setSuppliers]   = useState([]);
  const [allLocations, setLocations]   = useState([]);
  const [categories,   setCategories] = useState([]);
  const [bookingAssignments, setBookingAssignments] = useState([]);
  const [changingHotel,setChangingHotel] = useState(false);
  const [savingHotel,  setSavingHotel] = useState(false);

  const [panel,           setPanel]           = useState(null);
  const [showConfirmModal,setShowConfirmModal] = useState(false);
  const [confirming,      setConfirming]      = useState(false);
  const [publishing,      setPublishing]      = useState(false);
  const [questionItem,    setQuestionItem]    = useState(null);
  const [detailItem,      setDetailItem]      = useState(null);
  const [templates,       setTemplates]       = useState([]);
  const [applyingTemplate,setApplyingTemplate]= useState(false);
  const [savingTemplate,  setSavingTemplate]  = useState(false);
  const [skeletal,        setSkeletal]        = useState(null);
  const [applyingSkeletal,setApplyingSkeletal]= useState(false);
  const [renamingTemplateId, setRenamingTemplateId] = useState(null);
  const [renameValue,        setRenameValue]         = useState('');
  const [uploadingTicketId,  setUploadingTicketId]   = useState(null);
  const [crestDataUrl,       setCrestDataUrl]         = useState(null);
  const [exportingPdf,       setExportingPdf]         = useState(false);
  const [supplierFilter,     setSupplierFilter]       = useState(''); // '' = all suppliers
  const ticketInputRef      = useRef(null);
  const pendingTicketItemId = useRef(null);

  useEffect(() => {
    const q = query(collection(db, 'requests', requestId, 'itinerary'), orderBy('day'));
    return onSnapshot(q, (snap) => {
      setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }, [requestId]);

  useEffect(() => {
    return onSnapshot(doc(db, 'requests', requestId, 'itineraryMeta', 'full'), (snap) => {
      if (snap.exists()) setFullMeta(snap.data());
    });
  }, [requestId]);

  useEffect(() => {
    if (restricted) return;
    const q = query(collection(db, 'suppliers'), orderBy('name'));
    return onSnapshot(q, (snap) => {
      setSuppliers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }, [restricted]);

  useEffect(() => {
    if (restricted) return;
    const q = query(collection(db, 'locations'), orderBy('name'));
    return onSnapshot(q, (snap) => {
      setLocations(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }, [restricted]);

  useEffect(() => {
    if (restricted) return;
    const q = query(collection(db, 'bookingSuppliers'), where('requestId', '==', requestId));
    return onSnapshot(q, (snap) => {
      setBookingAssignments(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }, [restricted, requestId]);

  // Map supplierId → booking status for this tour
  const bookingStatusMap = useMemo(() => {
    const map = {};
    for (const a of bookingAssignments) {
      if (!a.supplierId) continue;
      // If there are multiple assignments for the same supplier, prefer confirmed > pending > declined
      const existing = map[a.supplierId];
      if (!existing) { map[a.supplierId] = a.status; continue; }
      if (existing === 'confirmed') continue;
      if (a.status === 'confirmed') { map[a.supplierId] = 'confirmed'; continue; }
      if (existing === 'pending') continue;
      map[a.supplierId] = a.status;
    }
    return map;
  }, [bookingAssignments]);

  useEffect(() => {
    if (restricted) return;
    const q = query(collection(db, 'supplierCategories'), orderBy('order', 'asc'));
    return onSnapshot(q, (snap) => {
      setCategories(
        snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((c) => c.active !== false)
          .map((c) => c.name)
      );
    });
  }, [restricted]);

  useEffect(() => {
    if (!request.crestUrl) { setCrestDataUrl(null); return; }
    fetch(request.crestUrl)
      .then((r) => r.blob())
      .then((blob) => new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result);
        reader.onerror = rej;
        reader.readAsDataURL(blob);
      }))
      .then(setCrestDataUrl)
      .catch(() => setCrestDataUrl(null));
  }, [request.crestUrl]);

  useEffect(() => {
    if (readOnly) return;
    const q = query(collection(db, 'itineraryTemplates'), orderBy('publishedAt', 'desc'), limit(6));
    getDocs(q).then((snap) => {
      setTemplates(snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((t) => t.id !== requestId)
      );
    }).catch(() => {});
  }, [readOnly, requestId]);

  useEffect(() => {
    if (readOnly) return;
    return onSnapshot(doc(db, 'requests', requestId, 'itineraryMeta', 'skeletal'), (snap) => {
      setSkeletal(snap.exists() ? snap.data() : null);
    });
  }, [readOnly, requestId]);

  // Recent templates with something to copy onto a tour of this length.
  const usableTemplates = useMemo(() => (
    templates
      .map((tpl) => ({ ...tpl, usableItems: itemsInsideTour(tpl.items, nDays) }))
      .filter((tpl) => tpl.usableItems.length > 0)
      .slice(0, 3)
  ), [templates, nDays]);

  // Suppliers on this itinerary, for the filter.
  const supplierOptions = useMemo(() => {
    const map = new Map();
    items.forEach((item) => {
      const key = supplierKey(item);
      if (key !== NO_SUPPLIER && !map.has(key)) map.set(key, item.supplierName || 'Supplier');
    });
    return [...map.entries()].map(([key, name]) => ({ key, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);
  const hasUnassigned = items.some((item) => supplierKey(item) === NO_SUPPLIER);
  // Drop a filter whose supplier has since left the itinerary.
  const activeFilter = supplierFilter && (supplierFilter === NO_SUPPLIER ? hasUnassigned : supplierOptions.some((o) => o.key === supplierFilter))
    ? supplierFilter : '';
  const filteredItems = useMemo(
    () => (activeFilter ? items.filter((item) => supplierKey(item) === activeFilter) : items),
    [items, activeFilter]
  );
  const filterName = activeFilter === NO_SUPPLIER ? t('itinerary.noSupplier')
    : supplierOptions.find((o) => o.key === activeFilter)?.name || '';

  const itemsByDay = useMemo(() => {
    const byDay = {};
    days.forEach((d) => { byDay[d] = []; });
    filteredItems.forEach((item) => {
      const idx = (item.day || 1) - 1;
      if (idx >= 0 && idx < nDays) byDay[idx].push(item);
    });
    Object.values(byDay).forEach((arr) => arr.sort((a, b) => (a.time || '').localeCompare(b.time || '')));
    return byDay;
  }, [filteredItems, nDays, days]);

  const outsideItems = useMemo(
    () => filteredItems.filter((item) => isOutsideTour(item, nDays)).sort((a, b) => (a.day - b.day) || (a.time || '').localeCompare(b.time || '')),
    [filteredItems, nDays]
  );

  const allGreen   = items.length > 0 && items.every((i) => isGreen(i.approvalStatus || 'pending'));
  const greenCount = items.filter((i) => isGreen(i.approvalStatus || 'pending') && !i.needsDetails).length;
  const needsDetailsCount = items.filter((i) => i.needsDetails).length;
  const canSend = allGreen && needsDetailsCount === 0;

  // Offered only while the itinerary is empty, apart from hotel meals.
  const onlyMeals = items.every((i) => i.itemType === 'meal');
  const skeletalItems = useMemo(
    () => (skeletal ? withoutExistingMeals(skeletalToItems(skeletal, nDays), items) : []),
    [skeletal, nDays, items]
  );

  const suppliersToEmail = useMemo(() => {
    const map = {};
    items.forEach((item) => {
      if (item.itemType === 'supplier' && item.requiresApproval && item.sendEmail
          && item.approvalStatus === 'pending' && item.supplierId && item.supplierEmail) {
        if (!map[item.supplierId]) {
          map[item.supplierId] = {
            supplierId: item.supplierId,
            supplierName: item.supplierName,
            supplierEmail: item.supplierEmail,
            items: [],
          };
        }
        map[item.supplierId].items.push(item);
      }
    });
    return Object.values(map);
  }, [items]);

  const hotel = fullMeta.hotelSupplierId ? {
    id:         fullMeta.hotelSupplierId,
    name:       fullMeta.hotelName,
    email:      fullMeta.hotelEmail,
    phone:      fullMeta.hotelPhone,
    address:    fullMeta.hotelAddress,
    googleLink: fullMeta.hotelGoogleLink,
  } : null;

  async function handleSelectHotel(supplier) {
    setSavingHotel(true);
    try {
      await setDoc(doc(db, 'requests', requestId, 'itineraryMeta', 'full'), {
        hotelSupplierId: supplier.id,
        hotelName:       supplier.name,
        hotelEmail:      supplier.email      || null,
        hotelPhone:      supplier.phone      || null,
        hotelAddress:    supplier.address    || null,
        hotelGoogleLink: supplier.googleLink || null,
      }, { merge: true });

      for (const dayIdx of days) {
        for (const meal of MEAL_DEFAULTS) {
          const existing = items.find((i) => i.day === dayIdx + 1 && i.title === meal.title && i.itemType === 'meal');
          if (existing?.needsDetails) {
            // Copied from the skeletal before a hotel was chosen.
            await updateDoc(doc(db, 'requests', requestId, 'itinerary', existing.id), {
              ...hotelMealFields(supplier), needsDetails: false, updatedAt: serverTimestamp(),
            });
          } else if (!existing) {
            await addDoc(collection(db, 'requests', requestId, 'itinerary'), {
              day: dayIdx + 1,
              time: meal.time,
              endTime: null,
              activityType: meal.activityType,
              title: meal.title,
              itemType: 'meal',
              ...hotelMealFields(supplier),
              requiresApproval: false,
              sendEmail: false,
              approvalStatus: 'not_required',
              notes: null,
              createdAt: serverTimestamp(),
            });
          }
        }
      }
      setChangingHotel(false);
    } finally {
      setSavingHotel(false);
    }
  }

  async function handleSaveItem(data) {
    if (panel?.item) {
      await updateDoc(doc(db, 'requests', requestId, 'itinerary', panel.item.id), {
        ...data, needsDetails: false, updatedAt: serverTimestamp(),
      });
    } else {
      await addDoc(collection(db, 'requests', requestId, 'itinerary'), {
        ...data, createdAt: serverTimestamp(),
      });
    }
  }

  async function handleDeleteItem(itemId) {
    const target = items.find((i) => i.id === itemId);
    if (target?.itemType !== 'meal' && !window.confirm('Remove this activity?')) return;
    await deleteDoc(doc(db, 'requests', requestId, 'itinerary', itemId));
  }

  async function handleOverride(item) {
    await updateDoc(doc(db, 'requests', requestId, 'itinerary', item.id), {
      approvalStatus: 'overridden', overriddenAt: serverTimestamp(),
    });
  }

  async function handleConfirmItinerary() {
    setConfirming(true);
    try {
      await addDoc(collection(db, 'itineraryConfirmRequests'), {
        requestId, createdAt: serverTimestamp(),
      });
      await setDoc(doc(db, 'requests', requestId, 'itineraryMeta', 'full'), {
        status: 'confirmed', confirmedAt: serverTimestamp(),
      }, { merge: true });
      await saveTemplate();
      setShowConfirmModal(false);
    } catch (err) {
      console.error(err);
      alert(`Failed to confirm: ${err.message}`);
    } finally {
      setConfirming(false);
    }
  }

  async function saveTemplate() {
    // Days past the end of this tour would reappear on whichever tour uses it.
    const templateItems = itemsInsideTour(items, nDays);
    if (templateItems.length === 0) return;
    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
    await setDoc(doc(db, 'itineraryTemplates', requestId), {
      requestId,
      label: [request.departureDate && fmtDate(request.departureDate), request.returnDate && fmtDate(request.returnDate)].filter(Boolean).join(' – ') || requestId,
      departureDate: request.departureDate || null,
      returnDate: request.returnDate || null,
      nDays,
      itemCount: templateItems.length,
      publishedAt: serverTimestamp(),
      // Fixtures belong to this tour, so a template never carries them.
      items: templateItems.map(({ id, fixtureIds, ...rest }) => rest),
    });
  }

  async function handleSaveAsTemplate() {
    setSavingTemplate(true);
    try {
      await saveTemplate();
    } catch (err) {
      console.error(err);
      alert(`Failed to save template: ${err.message}`);
    } finally {
      setSavingTemplate(false);
    }
  }

  async function handlePublish() {
    if (!canSend) return;
    setPublishing(true);
    try {
      await addDoc(collection(db, 'itineraryPublishRequests'), {
        requestId, createdAt: serverTimestamp(),
      });
      await setDoc(doc(db, 'requests', requestId, 'itineraryMeta', 'full'), {
        status: 'published', publishedAt: serverTimestamp(),
      }, { merge: true });
      await saveTemplate();
    } catch (err) {
      console.error(err);
      alert(`Failed to publish: ${err.message}`);
    } finally {
      setPublishing(false);
    }
  }

  async function handleApplyTemplate(template) {
    const skipped = (template.items || []).length - template.usableItems.length;
    const skippedNote = skipped > 0 ? ` ${skipped} on days after this tour ends will be left out.` : '';
    if (!window.confirm(`Copy ${template.usableItems.length} activities from this itinerary? All items will start as pending approval.${skippedNote}`)) return;
    setApplyingTemplate(true);
    try {
      for (const item of template.usableItems) {
        const hasSupplier = !!item.supplierId;
        const { fixtureIds, ...rest } = item; // older templates may still carry another tour's fixtures
        await addDoc(collection(db, 'requests', requestId, 'itinerary'), {
          ...rest,
          approvalStatus: hasSupplier ? 'pending' : 'not_required',
          requiresApproval: hasSupplier,
          sendEmail: hasSupplier,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
    } catch (err) {
      console.error(err);
      alert(`Failed to apply template: ${err.message}`);
    } finally {
      setApplyingTemplate(false);
    }
  }

  async function handleApplySkeletal() {
    if (!window.confirm(`Copy ${skeletalItems.length} activities from the skeletal? They will be marked "Needs details" until you fill them in.`)) return;
    setApplyingSkeletal(true);
    try {
      const batch = writeBatch(db);
      skeletalItems.forEach(({ day, time, title, isMeal }) => {
        const atHotel = isMeal && hotel;
        batch.set(doc(collection(db, 'requests', requestId, 'itinerary')), {
          day,
          time,
          endTime: null,
          activityType: isMeal ? MEAL_DEFAULTS[0].activityType : null,
          title,
          itemType: isMeal ? 'meal' : 'free_text',
          ...(atHotel ? hotelMealFields(hotel) : {}),
          requiresApproval: false,
          sendEmail: false,
          approvalStatus: 'not_required',
          notes: null,
          needsDetails: !atHotel,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      });
      await batch.commit();
    } catch (err) {
      console.error(err);
      alert(`Failed to copy the skeletal: ${err.message}`);
    } finally {
      setApplyingSkeletal(false);
    }
  }

  async function handleRenameTemplate(templateId) {
    const name = renameValue.trim();
    if (!name) return;
    try {
      await updateDoc(doc(db, 'itineraryTemplates', templateId), { customName: name });
      setTemplates((prev) => prev.map((t) => t.id === templateId ? { ...t, customName: name } : t));
    } catch (err) {
      console.error('Rename failed:', err);
    } finally {
      setRenamingTemplateId(null);
      setRenameValue('');
    }
  }

  async function handleLocationSave(itemId, newLocation) {
    const target = items.find((i) => i.id === itemId);
    try {
      await updateDoc(doc(db, 'requests', requestId, 'itinerary', itemId), {
        locationName: newLocation || null,
        title: newLocation || target?.activityType || target?.title || null,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Failed to save location:', err);
    }
  }

  async function handleLeaderNotesSave(itemId, leaderNotes) {
    try {
      await updateDoc(doc(db, 'requests', requestId, 'itinerary', itemId), {
        leaderNotes: leaderNotes || null,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Failed to save tour leader notes:', err);
      alert('Could not save the note. Please try again.');
    }
  }

  function renderItemTable(rows, editDayIdx) {
    return (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[800px]">
          <thead>
            <tr className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <th className="px-4 py-2.5 text-left text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider w-20">{t('itinerary.colTime')}</th>
              <th className="px-4 py-2.5 text-left text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">{t('itinerary.colActivity')}</th>
              <th className="px-4 py-2.5 text-left text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">{t('itinerary.colLeaderNotes')}</th>
              <th className="px-4 py-2.5 text-left text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">{t('itinerary.colLocation')}</th>
              <th className="px-4 py-2.5 text-left text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">{t('itinerary.colSupplier')}</th>
              <th className="px-4 py-2.5 text-left text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider w-32">{t('itinerary.colStatus')}</th>
              <th className="px-4 py-2.5 text-left text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider w-24">{t('itinerary.colTicket')}</th>
              {!readOnly && <th className="w-24" />}
            </tr>
          </thead>
          <tbody>
            {rows.map((item) => (
              <ItineraryRow
                key={item.id}
                item={item}
                readOnly={readOnly}
                canUploadTickets={canUploadTickets}
                onEdit={(i) => setPanel({ dayIdx: editDayIdx, item: i })}
                onDelete={handleDeleteItem}
                onOverride={handleOverride}
                onViewQuestion={(i) => setQuestionItem(i)}
                onDetail={(i) => setDetailItem(i)}
                onLocationSave={handleLocationSave}
                onLeaderNotesSave={handleLeaderNotesSave}
                onUploadTicket={handleTicketUploadClick}
                onRemoveTicket={handleTicketRemove}
                isUploadingTicket={uploadingTicketId === item.id}
                bookingStatusMap={bookingStatusMap}
                matches={matches}
              />
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  async function handleExportGroupPdf(filtered = false) {
    setExportingPdf(true);
    try {
      generateGroupItineraryPdf(filtered ? filteredItems : items, request, crestDataUrl, { fixturesById, teamsById: tourTeams.teamsById },
        filtered ? { filterLabel: filterName } : {});
    } catch (err) {
      console.error('Group PDF export failed:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setExportingPdf(false);
    }
  }

  function handleTicketUploadClick(itemId) {
    pendingTicketItemId.current = itemId;
    ticketInputRef.current?.click();
  }

  async function handleTicketFileChange(e) {
    const file = e.target.files?.[0];
    const itemId = pendingTicketItemId.current;
    e.target.value = '';
    if (!file || !itemId) return;
    setUploadingTicketId(itemId);
    try {
      const path = `requests/${requestId}/tickets/${itemId}`;
      const sRef = storageRef(storage, path);
      await uploadBytes(sRef, file);
      const url = await getDownloadURL(sRef);
      await updateDoc(doc(db, 'requests', requestId, 'itinerary', itemId), {
        ticketUrl: url,
        ticketStoragePath: path,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Ticket upload failed:', err);
      alert('Failed to upload ticket. Please try again.');
    } finally {
      setUploadingTicketId(null);
      pendingTicketItemId.current = null;
    }
  }

  async function handleTicketRemove(itemId, storagePath) {
    if (!window.confirm('Remove this ticket?')) return;
    try {
      if (storagePath) {
        await deleteObject(storageRef(storage, storagePath)).catch(() => {});
      }
      await updateDoc(doc(db, 'requests', requestId, 'itinerary', itemId), {
        ticketUrl: null,
        ticketStoragePath: null,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Ticket remove failed:', err);
      alert('Failed to remove ticket. Please try again.');
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">

      {/* Hotel assignment (staff only) */}
      {!readOnly && (
        <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-[12px] p-4">
          <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-1">Hotel / Accommodation</p>
          <p className="text-[11px] text-[#9CA3AF] mb-3">Assigned once for the tour — meals auto-link to this supplier</p>

          {hotel && !changingHotel ? (
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[14px] font-bold text-navy">{hotel.name}</p>
                {hotel.address && <p className="text-[12px] text-[#6B7280] mt-0.5">📍 {hotel.address}</p>}
                <div className="flex flex-wrap gap-4 mt-1">
                  {hotel.phone && <p className="text-[12px] text-[#6B7280]">📞 {hotel.phone}</p>}
                  {hotel.email && <p className="text-[12px] text-[#6B7280]">{hotel.email}</p>}
                  {hotel.googleLink && (
                    <a href={hotel.googleLink} target="_blank" rel="noopener noreferrer" className="text-[12px] text-pb hover:underline">🗺 Maps</a>
                  )}
                </div>
              </div>
              <button onClick={() => setChangingHotel(true)} className="text-[11px] text-[#9CA3AF] hover:text-pb shrink-0 transition">
                Change
              </button>
            </div>
          ) : (
            <div>
              <SupplierSearchInput
                suppliers={allSuppliers}
                selectedId=""
                onSelect={handleSelectHotel}
                placeholder="Search and select hotel supplier…"
                bookingStatusMap={bookingStatusMap}
              />
              {savingHotel && <p className="text-[11px] text-[#9CA3AF] mt-2">Setting hotel and creating meal slots…</p>}
              {changingHotel && !savingHotel && (
                <button onClick={() => setChangingHotel(false)} className="mt-2 text-[11px] text-[#9CA3AF] hover:text-[#374151]">
                  Cancel
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Skeletal copy — shown while the itinerary has nothing but hotel meals */}
      {!readOnly && onlyMeals && skeletalItems.length > 0 && (
        <div className="bg-[#EFF6FF] border border-blue-200 rounded-[12px] p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-[220px] flex-1">
            <p className="text-[11px] font-bold text-navy uppercase tracking-wider mb-1">Start from the skeletal itinerary</p>
            <p className="text-[12px] text-[#374151]">
              Copy {skeletalItems.length} {skeletalItems.length === 1 ? 'activity' : 'activities'} from this tour's skeletal, then add the exact times, suppliers and locations. Nothing is emailed to suppliers.
            </p>
          </div>
          <button type="button" disabled={applyingSkeletal} onClick={handleApplySkeletal}
            className="px-4 py-2 bg-navy hover:bg-pb text-white text-[12px] font-bold rounded-[9px] transition disabled:opacity-60 shrink-0">
            {applyingSkeletal ? 'Copying…' : 'Copy from skeletal'}
          </button>
        </div>
      )}

      {/* Template picker — shown only when itinerary is empty */}
      {!readOnly && items.length === 0 && usableTemplates.length > 0 && (
        <div className="bg-[#FFFBEB] border border-amber-200 rounded-[12px] p-4">
          <p className="text-[11px] font-bold text-[#92400E] uppercase tracking-wider mb-1">Start from a recent itinerary</p>
          <p className="text-[12px] text-[#78350F] mb-3">
            Copy the structure from a previously published itinerary. All activities will start as pending approval so suppliers can reconfirm.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {usableTemplates.map((t) => (
              <div key={t.id} className="bg-white border border-amber-200 rounded-[10px] overflow-hidden">
                {renamingTemplateId === t.id ? (
                  <div className="p-3 space-y-2">
                    <input
                      autoFocus
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleRenameTemplate(t.id); if (e.key === 'Escape') { setRenamingTemplateId(null); setRenameValue(''); } }}
                      className="w-full border border-amber-300 rounded-[6px] px-2 py-1 text-[12px] focus:outline-none focus:ring-2 focus:ring-amber-400"
                      placeholder="Template name…"
                    />
                    <div className="flex gap-1.5">
                      <button type="button" onClick={() => handleRenameTemplate(t.id)} className="px-2 py-1 text-[11px] font-semibold bg-[#92400E] text-white rounded-[5px] hover:bg-amber-800 transition">Save</button>
                      <button type="button" onClick={() => { setRenamingTemplateId(null); setRenameValue(''); }} className="px-2 py-1 text-[11px] font-semibold text-[#6B7280] border border-[#E5E7EB] rounded-[5px] hover:bg-[#F9FAFB] transition">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-1">
                    <button
                      type="button"
                      disabled={applyingTemplate}
                      onClick={() => handleApplyTemplate(t)}
                      className="flex-1 text-left p-3 hover:bg-amber-50 transition disabled:opacity-60"
                    >
                      <p className="text-[12px] font-bold text-[#111827] leading-snug">{t.customName || t.label || t.requestId}</p>
                      <p className="text-[11px] text-[#9CA3AF] mt-0.5">{t.usableItems.length} {t.usableItems.length === 1 ? 'activity' : 'activities'} · {t.nDays} days</p>
                    </button>
                    <button
                      type="button"
                      title="Rename"
                      onClick={() => { setRenamingTemplateId(t.id); setRenameValue(t.customName || t.label || ''); }}
                      className="p-2 mt-1 mr-1 text-[#9CA3AF] hover:text-[#92400E] transition flex-shrink-0"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
          {applyingTemplate && <p className="text-[11px] text-[#92400E] mt-2">Copying activities…</p>}
        </div>
      )}

      {/* Progress + action bar (staff only) */}
      {!readOnly && (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            {items.length > 0 && (
              <>
                <div className="flex gap-0.5 flex-wrap max-w-[200px]">
                  {items.map((item, i) => (
                    <span key={i} className="w-2 h-2 rounded-full inline-block"
                      style={{ background: item.needsDetails ? NEEDS_DETAILS_DOT : (STATUS[item.approvalStatus] || STATUS.pending).dot }} />
                  ))}
                </div>
                <span className="text-[12px] text-[#6B7280] font-medium">
                  {greenCount}/{items.length} approved
                  {needsDetailsCount > 0 && <span className="text-[#D97706]"> · {needsDetailsCount} need{needsDetailsCount === 1 ? 's' : ''} details</span>}
                </span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {items.length > 0 && (
              <button onClick={handleSaveAsTemplate} disabled={savingTemplate}
                className="px-4 py-2 border border-[#E5E7EB] text-[#6B7280] hover:text-pb hover:border-pb text-[12px] font-semibold rounded-[9px] transition disabled:opacity-60">
                {savingTemplate ? 'Saving…' : '📋 Save as template'}
              </button>
            )}
            {fullMeta.status !== 'published' && (
              <button onClick={() => setShowConfirmModal(true)}
                className="px-4 py-2 bg-navy hover:bg-pb text-white text-[12px] font-bold rounded-[9px] transition">
                Confirm Itinerary
              </button>
            )}
            {canSend ? (
              <button onClick={handlePublish} disabled={publishing || fullMeta.status === 'published'}
                className="px-4 py-2 bg-sg text-white text-[12px] font-bold rounded-[9px] hover:opacity-90 transition disabled:opacity-60">
                {fullMeta.status === 'published' ? '✓ Sent to Tour Leader' : (publishing ? 'Sending…' : 'Send to Tour Leader →')}
              </button>
            ) : items.length > 0 && (
              <p className="text-[11px] text-[#9CA3AF] text-right max-w-[200px]">
                {needsDetailsCount > 0
                  ? 'Fill in the activities marked "Needs details" before sending to the tour leader'
                  : 'All activities must be approved before sending to the tour leader'}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Supplier filter + export for group — every portal that shows this itinerary */}
      {items.length > 0 && (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap" data-preview-allow>
            <label htmlFor="itin-supplier-filter" className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">{t('itinerary.filterSupplier')}</label>
            <select
              id="itin-supplier-filter"
              value={activeFilter}
              onChange={(e) => setSupplierFilter(e.target.value)}
              className="border border-[#E5E7EB] rounded-[9px] px-2.5 py-1.5 text-[12px] text-[#374151] bg-white focus:outline-none focus:ring-2 focus:ring-pb max-w-[240px]"
            >
              <option value="">{t('itinerary.allSuppliers')}</option>
              {supplierOptions.map((o) => <option key={o.key} value={o.key}>{o.name}</option>)}
              {hasUnassigned && <option value={NO_SUPPLIER}>{t('itinerary.noSupplier')}</option>}
            </select>
            {activeFilter && (
              <>
                <span className="text-[11px] text-[#6B7280]">{t('itinerary.showingN', { n: filteredItems.length, total: items.length })}</span>
                <button onClick={() => setSupplierFilter('')} className="text-[11px] font-semibold text-pb hover:text-navy">{t('itinerary.clearFilter')}</button>
              </>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {[false, ...(activeFilter ? [true] : [])].map((filtered) => (
              <button
                key={String(filtered)}
                onClick={() => handleExportGroupPdf(filtered)}
                disabled={exportingPdf || (filtered && filteredItems.length === 0)}
                className="inline-flex items-center gap-2 px-4 py-2 border border-[#E5E7EB] text-[#6B7280] hover:text-pb hover:border-pb text-[12px] font-semibold rounded-[9px] transition disabled:opacity-60"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                {exportingPdf ? t('itinerary.generating')
                  : !activeFilter ? t('itinerary.exportGroup')
                  : filtered ? t('itinerary.exportFiltered', { name: filterName })
                  : t('itinerary.exportAll')}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Day sections */}
      {days.length === 0 ? (
        <p className="text-[13px] text-[#9CA3AF] py-4">{t('itinerary.noDates')}</p>
      ) : (
        days.map((dayIdx) => {
          const dayItems = itemsByDay[dayIdx] || [];
          return (
            <div key={dayIdx} className="bg-white border border-[#E5E7EB] rounded-[12px] overflow-hidden">
              <div className="bg-navy px-4 py-3 flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold text-blue-300 uppercase tracking-wider">{t('itinerary.dayN', { n: dayIdx + 1 })}</span>
                  <h4 className="text-[13px] font-bold text-white mt-0.5">{dayLabel(request.departureDate, dayIdx, t.dateLocale)}</h4>
                </div>
                {!readOnly && (
                  <button onClick={() => setPanel({ dayIdx, item: null })}
                    className="text-[12px] font-semibold text-blue-200 hover:text-white transition">
                    + Add Activity
                  </button>
                )}
              </div>

              {dayItems.length === 0 ? (
                <p className="px-4 py-4 text-[12px] text-[#9CA3AF] italic">
                  {readOnly ? t('itinerary.noActivities') : 'No activities yet — click "+ Add Activity" above to start.'}
                </p>
              ) : (
                renderItemTable(dayItems, dayIdx)
              )}

              {!readOnly && dayItems.length > 0 && (
                <div className="px-4 py-2 border-t border-[#F3F4F6]">
                  <button onClick={() => setPanel({ dayIdx, item: null })}
                    className="text-[12px] font-medium text-[#9CA3AF] hover:text-pb transition">
                    + Add another activity to Day {dayIdx + 1}
                  </button>
                </div>
              )}
            </div>
          );
        })
      )}

      {/* Activities on days this tour doesn't have (staff only) */}
      {!readOnly && outsideItems.length > 0 && (
        <div className="bg-white border border-amber-300 rounded-[12px] overflow-hidden">
          <div className="bg-[#FFFBEB] border-b border-amber-200 px-4 py-3">
            <h4 className="text-[13px] font-bold text-[#92400E]">
              Outside tour dates: {[...new Set(outsideItems.map((i) => `Day ${i.day}`))].join(', ')}
            </h4>
            <p className="text-[12px] text-[#78350F] mt-0.5">
              This tour only has {nDays} {nDays === 1 ? 'day' : 'days'}, so these activities aren't on the itinerary. Edit one to move it to the last day, or remove it.
            </p>
          </div>
          {renderItemTable(outsideItems, nDays - 1)}
        </div>
      )}

      {/* Status legend (staff only) */}
      {!readOnly && items.length > 0 && (
        <div className="flex gap-4 flex-wrap pt-1">
          {Object.entries(STATUS)
            .filter(([k]) => !['overridden'].includes(k))
            .map(([, s]) => (
              <div key={s.label} className="flex items-center gap-1.5 text-[11px] text-[#9CA3AF]">
                <span className="w-2 h-2 rounded-full inline-block" style={{ background: s.dot }} />
                {s.label}
              </div>
            ))}
          {needsDetailsCount > 0 && (
            <div className="flex items-center gap-1.5 text-[11px] text-[#9CA3AF]">
              <span className="w-2 h-2 rounded-full inline-block" style={{ background: NEEDS_DETAILS_DOT }} />
              Needs details
            </div>
          )}
          <span className="text-[11px] text-[#9CA3AF]">· Hover a row for edit / confirm / remove</span>
        </div>
      )}

      {/* Modals */}
      {panel && (
        <ItemPanel
          dayIdx={panel.dayIdx}
          item={panel.item}
          request={request}
          allSuppliers={allSuppliers}
          allLocations={allLocations}
          categories={categories}
          bookingStatusMap={bookingStatusMap}
          onSave={handleSaveItem}
          onClose={() => setPanel(null)}
          tour={{ requestId, teams: tourTeams.teams, teamsById: tourTeams.teamsById, fixtures: tourTeams.fixtures }}
          fixtureContacts={fixtureContacts}
        />
      )}
      {showConfirmModal && (
        <ConfirmItineraryModal
          suppliersToEmail={suppliersToEmail}
          confirming={confirming}
          onConfirm={handleConfirmItinerary}
          onClose={() => setShowConfirmModal(false)}
        />
      )}
      {questionItem && (
        <QuestionModal item={questionItem} requestId={requestId} onClose={() => setQuestionItem(null)} />
      )}
      {detailItem && (
        <SupplierDetailModal item={detailItem} onClose={() => setDetailItem(null)} />
      )}

      {/* Hidden file input for ticket uploads */}
      <input
        ref={ticketInputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="hidden"
        onChange={handleTicketFileChange}
      />
    </div>
  );
}
